import { Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { ILearningUserState } from '../models/knowledge.models';
import { KnowledgeLearningSystemService } from './knowledge-learning-system.service';
import { SourceCoreDexieService } from './source-core-dexie.service';
import { UserStateDexieService } from './user-state-dexie.service';
import { isEmpty } from 'lodash';

import { FiltersConfig, HttpCoreService } from '@dataclouder/ngx-core';
import { MasterStateConfig } from './master-state.providers';

export type StateCoreData<T> = { state: ILearningUserState; core: T };

export class MasterStateService<T extends { id?: string }> {
  // Signals
  public userState: WritableSignal<Record<string, ILearningUserState>> = signal({});
  public sourceCore: WritableSignal<Record<string, T>> = signal({});
  // Computed signals
  public stateCore: Signal<Record<string, StateCoreData<T>>> = computed(() => {
    const userState = this.userState();
    const sourceCore = this.sourceCore();
    const result: Record<string, StateCoreData<T>> = {};

    if (isEmpty(userState) || isEmpty(sourceCore)) {
      return {};
    }

    for (const id in userState) {
      if (sourceCore[id]) {
        result[id] = { state: userState[id], core: sourceCore[id] };
      }
    }
    return result;
  });

  // Services Declarations
  private knowledgeLearningSystemService: KnowledgeLearningSystemService;
  private httpCoreService: HttpCoreService;
  private userStateDexieService: UserStateDexieService;
  private sourceCoreDexieService: SourceCoreDexieService;

  constructor(private config: MasterStateConfig) {
    this.knowledgeLearningSystemService = inject(KnowledgeLearningSystemService);
    this.httpCoreService = inject(HttpCoreService);
    this.userStateDexieService = inject(UserStateDexieService);
    this.sourceCoreDexieService = inject(SourceCoreDexieService);
    this.instanciateDexieTables(this.config.pathTable);
  }

  public async instanciateDexieTables(pathTable: string) {
    if (!!pathTable) {
      await this.userStateDexieService.ensureTable(pathTable);
      await this.sourceCoreDexieService.ensureTable(pathTable);
    }
  }

  public setConfig(config: Partial<MasterStateConfig>): void {
    this.config = { ...this.config, ...config };
    this.clearMemory();

    // Create new instances with the new table name
    this.instanciateDexieTables(this.config.pathTable);
  }

  // _____   METHODS FOR USER STATE _____

  public async getUserState(): Promise<Record<string, ILearningUserState>> {
    // 1) Get it from memory
    if (!isEmpty(this.userState())) {
      return this.userState();
    }
    // 2) Get it from DB
    const res = await this.userStateDexieService.getUserStateDbData(this.config.pathTable);
    if (isEmpty(res)) {
      console.log('No data found in DB', res);

      // 3) Get it from Backend API
      const learningExperiences = await this.knowledgeLearningSystemService.getKnowledgeState(this.config.userStateEndPoint, this.config.pathTable);
      console.log('learningExperiences', learningExperiences);

      if (!isEmpty(learningExperiences)) {
        // 3.1) Save it to DB
        await this.userStateDexieService.bulkUpsertKnowledgeUserStates(this.config.pathTable, Object.values(learningExperiences));
        this.userState.set(learningExperiences);
        return this.userState();
      } else {
        // No data in local db and backend db so is first time.
      }
    }
    this.userState.set(res);
    return res;
  }

  public async updateUserState(state: ILearningUserState): Promise<any> {
    // not sure what to return here.
    const res = await this.userStateDexieService.upsertKnowledgeUserState(this.config.pathTable, state);
    this.userState.update((userState) => ({ ...userState, [state.id]: state }));
    return res;
  }

  public async updateManyUserStates(userState: ILearningUserState[]): Promise<string[]> {
    const idUpserted = await this.userStateDexieService.bulkUpsertKnowledgeUserStates(this.config.pathTable, userState);
    // Check what structure and i'm using here.
    this.userState.update((currentUserState) => {
      const newUserState = { ...currentUserState };
      for (const word of userState) {
        newUserState[word.id] = word;
      }
      return newUserState;
    });
    return idUpserted;
  }

  public async removeUserState(id: string): Promise<void> {
    // Remove from memory
    this.userState.update((userState) => {
      const newUserState = { ...userState };
      delete newUserState[id];
      return newUserState;
    });
    // Remove from DB
    await this.userStateDexieService.deleteKnowledgeUserState(this.config.pathTable, id);
  }
  // _________________________________________________

  // _____   METHODS FOR SOURCE CORE STATE _____

  async getSourceCore(): Promise<Record<string, T>> {
    // 1) Return current Dict source code if exists
    if (!isEmpty(this.sourceCore())) {
      return this.sourceCore();
    } else {
      // 2) Get it from DB
      const sourceCoreDb = await this.sourceCoreDexieService.getLearningSourceDbData<T>(this.config.pathTable);
      if (!isEmpty(sourceCoreDb)) {
        this.sourceCore.set(sourceCoreDb);
        return sourceCoreDb;
      }
      // 3) This is first time Get learning state first, to know what to query.
      const learningState = await this.getUserState();
      if (!isEmpty(learningState)) {
        const ids = Object.keys(learningState);
        console.log('learningState query with this ids. ', ids);
        const sourceCoreEntries = await this.getCoreKnowledgeByIds(ids);
        if (!isEmpty(sourceCoreEntries)) {
          await this.sourceCoreDexieService.bulkUpsertKnowledgeCoreData<T>(this.config.pathTable, sourceCoreEntries);

          const sourceCoreDict = sourceCoreEntries.reduce((acc: Record<string, T>, item: T) => {
            acc[item.id] = item;
            return acc;
          }, {});

          this.sourceCore.set(sourceCoreDict);
          return this.sourceCore();
        }
      } else {
        // Nothing to do, user needs to interact with the app to get the data.
        return {};
      }

      return {};
    }
  }

  public async getSourceCoreArray(): Promise<T[]> {
    const sourceCore = await this.getSourceCore();
    return Object.values(sourceCore);
  }

  public async updateSourceCore(sourceCore: T) {
    this.sourceCore.update((current) => ({ ...current, [sourceCore.id]: sourceCore }));
    await this.sourceCoreDexieService.bulkUpsertKnowledgeCoreData<T>(this.config.pathTable, [sourceCore]);
  }

  public async updateManySourceCore(sourceCore: T[]) {
    if (isEmpty(this.sourceCore())) {
      // Should be already loaded, but just in case.
      await this.getSourceCore();
    }
    this.sourceCore.update((current) => {
      const newSourceCore = { ...current };
      for (const item of sourceCore) {
        newSourceCore[item.id] = item;
      }
      return newSourceCore;
    });
    await this.sourceCoreDexieService.bulkUpsertKnowledgeCoreData<T>(this.config.pathTable, sourceCore);
  }

  public async removeSourceCore(id: string) {
    this.sourceCore.update((current) => {
      const newSourceCore = { ...current };
      delete newSourceCore[id];
      return newSourceCore;
    });
    await this.sourceCoreDexieService.deleteKnowledgeSource(this.config.pathTable, id);
  }
  // ___________________________________________________________________________

  // _____ METHODS FOR CORE STATE ___________

  public async getStateCore(): Promise<Record<string, StateCoreData<T>>> {
    console.log('stateCore', this.config.pathTable, this.stateCore());

    await this.getUserState();
    await this.getSourceCore();
    console.log('stateCore', this.config.pathTable, this.stateCore());
    return this.stateCore();
  }

  // ___________________________________________________________________________

  clearMemory(): void {
    this.userState.set({});
    this.sourceCore.set({});
  }

  clearDB(): void {
    this.userStateDexieService.clearAllData(this.config.pathTable);
    this.sourceCoreDexieService.clearAllData(this.config.pathTable);
    alert('Not sure is is deliting all data. ');
  }

  removeDBs(): void {
    this.userStateDexieService.deleteDatabase();
    this.sourceCoreDexieService.deleteDatabase();
  }

  public clearMemoryAndRemoveDBs(): void {
    this.clearMemory();
    this.removeDBs();
    // this.clearDB();
  }

  private async getCoreKnowledgeByIds(ids: string[]): Promise<T[]> {
    const filters: FiltersConfig = {
      filters: {
        id: {
          $in: ids,
        },
      },
    };
    const res = await this.httpCoreService.post<{ rows: T[] }>(this.config.sourceCoreEndPoint, filters);
    return res.rows;
  }
}
